import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { standardToVertex } from '../../functions/quadratic/quadratic.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { ComputedCollection, ComputedCollectionItem } from '../../../context/computed-collection';
import type { ExtremaParams } from './extrema.model';

/**
 * Creates extrema points (maxima and minima) for functions.
 *
 * Subparts:
 * - `maxima`: Points at local maxima
 * - `minima`: Points at local minima
 */
export class ExtremaCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'extrema';
  override readonly computedValueKeys: readonly string[] = buildComputedValueKeys(3);
  override readonly computedCollectionKeys = [
    { name: 'maxima', properties: ['x', 'y'] },
    { name: 'minima', properties: ['x', 'y'] },
  ] as const;

  private readonly extremaCount = 3;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as ExtremaParams | undefined;
    if (!params?.of) {
      throw new Error('Extrema feature requires params.of to specify the parent function');
    }
    const parentFn: FunctionElement = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    if (parentFn.type === 'sine') {
      for (let n = -this.extremaCount; n <= this.extremaCount; n++) {
        objects.push(...this.createSinePoint(context, display, ['maxima'], parentFn, n, 'maxima'));
        objects.push(...this.createSinePoint(context, display, ['minima'], parentFn, n, 'minima'));
      }
    } else if (parentFn.type === 'cosine') {
      for (let n = -this.extremaCount; n <= this.extremaCount; n++) {
        objects.push(...this.createCosinePoint(context, display, ['maxima'], parentFn, n, 'maxima'));
        objects.push(...this.createCosinePoint(context, display, ['minima'], parentFn, n, 'minima'));
      }
    } else if (parentFn.type === 'quadratic' || parentFn.type === 'quadratic-vertex') {
      const isMax = () => parentFn.getParams()['a'] < 0;
      const subpart = () => (isMax() ? 'maxima' : 'minima');
      const style = resolveFeatureStyle('point', this.featureType, context, display, [subpart()]);
      const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, [subpart()]);
      const result = elementFactory.point({
        coords: [() => getQuadraticVertexX(parentFn), () => getQuadraticVertexY(parentFn)],
        name: () => (isMax() ? 'max' : 'min'),
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    } else if (parentFn.type === 'cubic-general' || parentFn.type === 'cubic') {
      // Local max point
      const maxStyle = resolveFeatureStyle('point', this.featureType, context, display, ['maxima']);
      const maxVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['maxima']);
      const maxResult = elementFactory.point({
        coords: [() => getCubicExtremaX(parentFn, 'max'), () => getCubicExtremaY(parentFn, 'max')],
        name: 'max',
        style: { ...maxStyle, visible: maxVisible },
      });
      objects.push(maxResult.point);
      if (maxResult.label) {
        objects.push(maxResult.label);
      }
      // Local min point
      const minStyle = resolveFeatureStyle('point', this.featureType, context, display, ['minima']);
      const minVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['minima']);
      const minResult = elementFactory.point({
        coords: [() => getCubicExtremaX(parentFn, 'min'), () => getCubicExtremaY(parentFn, 'min')],
        name: 'min',
        style: { ...minStyle, visible: minVisible },
      });
      objects.push(minResult.point);
      if (minResult.label) {
        objects.push(minResult.label);
      }
    }

    const computedValues: Record<string, () => number> = {};
    if (parentFn.type === 'cubic-general' || parentFn.type === 'cubic') {
      computedValues['maximaCount'] = () => (getCubicDiscriminant(parentFn) >= 0 ? 1 : 0);
      computedValues['minimaCount'] = () => (getCubicDiscriminant(parentFn) >= 0 ? 1 : 0);
      computedValues['maxima0X'] = () => getCubicExtremaX(parentFn, 'max');
      computedValues['maxima0Y'] = () => getCubicExtremaY(parentFn, 'max');
      computedValues['minima0X'] = () => getCubicExtremaX(parentFn, 'min');
      computedValues['minima0Y'] = () => getCubicExtremaY(parentFn, 'min');
    } else if (parentFn.type === 'quadratic' || parentFn.type === 'quadratic-vertex') {
      computedValues['count'] = () => 1;
      computedValues['x0'] = () => getQuadraticVertexX(parentFn);
      computedValues['y0'] = () => getQuadraticVertexY(parentFn);
    } else if (parentFn.type === 'sine' || parentFn.type === 'cosine') {
      const cnt = this.extremaCount;
      const totalPoints = 2 * cnt + 1;
      computedValues['maximaCount'] = () => totalPoints;
      computedValues['minimaCount'] = () => totalPoints;
      computedValues['maxY'] = () => parentFn.getParams()['d'] + parentFn.getParams()['a'];
      computedValues['minY'] = () => parentFn.getParams()['d'] - parentFn.getParams()['a'];

      for (let n = -cnt; n <= cnt; n++) {
        const idx = n + cnt;
        if (parentFn.type === 'sine') {
          // Sine maxima: x = c + π/(2b) + 2πn/b, y = d + a
          computedValues[`maxima${idx}X`] = ((nn) => () => {
            const { b, c } = parentFn.getParams();
            return c + Math.PI / (2 * b) + (2 * Math.PI * nn) / b;
          })(n);
          computedValues[`maxima${idx}Y`] = () => {
            const { a, d } = parentFn.getParams();
            return d + a;
          };
          // Sine minima: x = c + 3π/(2b) + 2πn/b, y = d - a
          computedValues[`minima${idx}X`] = ((nn) => () => {
            const { b, c } = parentFn.getParams();
            return c + (3 * Math.PI) / (2 * b) + (2 * Math.PI * nn) / b;
          })(n);
          computedValues[`minima${idx}Y`] = () => {
            const { a, d } = parentFn.getParams();
            return d - a;
          };
        } else {
          // Cosine maxima: x = c + 2πn/b, y = d + a
          computedValues[`maxima${idx}X`] = ((nn) => () => {
            const { b, c } = parentFn.getParams();
            return c + (2 * Math.PI * nn) / b;
          })(n);
          computedValues[`maxima${idx}Y`] = () => {
            const { a, d } = parentFn.getParams();
            return d + a;
          };
          // Cosine minima: x = c + π/b + 2πn/b, y = d - a
          computedValues[`minima${idx}X`] = ((nn) => () => {
            const { b, c } = parentFn.getParams();
            return c + Math.PI / b + (2 * Math.PI * nn) / b;
          })(n);
          computedValues[`minima${idx}Y`] = () => {
            const { a, d } = parentFn.getParams();
            return d - a;
          };
        }
      }
    }

    // Build computed collections for indexed access (e.g. f-extrema.maxima.0.x)
    const collections: Record<string, ComputedCollection> = {};
    if (parentFn.type === 'cubic-general' || parentFn.type === 'cubic') {
      const hasExtrema = () => getCubicDiscriminant(parentFn) >= 0;
      collections['maxima'] = {
        getCount: () => (hasExtrema() ? 1 : 0),
        maxItems: 1,
        getItem: (i) =>
          i === 0 && hasExtrema()
            ? { x: () => getCubicExtremaX(parentFn, 'max'), y: () => getCubicExtremaY(parentFn, 'max') }
            : undefined,
        properties: ['x', 'y'],
      };
      collections['minima'] = {
        getCount: () => (hasExtrema() ? 1 : 0),
        maxItems: 1,
        getItem: (i) =>
          i === 0 && hasExtrema()
            ? { x: () => getCubicExtremaX(parentFn, 'min'), y: () => getCubicExtremaY(parentFn, 'min') }
            : undefined,
        properties: ['x', 'y'],
      };
    } else if (parentFn.type === 'quadratic' || parentFn.type === 'quadratic-vertex') {
      // Single-item collections for quadratic
      const isMax = () => parentFn.getParams()['a'] < 0;
      const getVertexItem = (): ComputedCollectionItem => ({
        x: () => getQuadraticVertexX(parentFn),
        y: () => getQuadraticVertexY(parentFn),
      });
      collections['maxima'] = {
        getCount: () => (isMax() ? 1 : 0),
        maxItems: 1,
        getItem: (i) => (i === 0 && isMax() ? getVertexItem() : undefined),
        properties: ['x', 'y'],
      };
      collections['minima'] = {
        getCount: () => (isMax() ? 0 : 1),
        maxItems: 1,
        getItem: (i) => (i === 0 && !isMax() ? getVertexItem() : undefined),
        properties: ['x', 'y'],
      };
    } else if (parentFn.type === 'sine' || parentFn.type === 'cosine') {
      const cnt = this.extremaCount;
      const totalPoints = 2 * cnt + 1;
      const buildPeriodicCollection = (type: 'maxima' | 'minima'): ComputedCollection => ({
        getCount: () => totalPoints,
        maxItems: totalPoints,
        getItem: (i) => {
          if (i < 0 || i >= totalPoints) {
            return undefined;
          }
          const n = i - cnt;
          const p = parentFn.getParams();
          const { a, b, c, d } = p;
          if (parentFn.type === 'sine') {
            const x =
              type === 'maxima'
                ? c + Math.PI / (2 * b) + (2 * Math.PI * n) / b
                : c + (3 * Math.PI) / (2 * b) + (2 * Math.PI * n) / b;
            const y = type === 'maxima' ? d + a : d - a;
            return { x: () => x, y: () => y };
          } else {
            const x = type === 'maxima' ? c + (2 * Math.PI * n) / b : c + Math.PI / b + (2 * Math.PI * n) / b;
            const y = type === 'maxima' ? d + a : d - a;
            return { x: () => x, y: () => y };
          }
        },
        properties: ['x', 'y'],
      });
      collections['maxima'] = buildPeriodicCollection('maxima');
      collections['minima'] = buildPeriodicCollection('minima');
    }

    return {
      id,
      type: 'extrema',
      objects,
      getComputedValues: () => computedValues,
      getComputedCollections: Object.keys(collections).length > 0 ? () => collections : undefined,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }

  private createSinePoint(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[],
    parentFn: FunctionElement,
    n: number,
    type: 'maxima' | 'minima',
  ): GeometryElement[] {
    const { elementFactory, featureTheme } = context;
    const style = resolveFeatureStyle('point', this.featureType, context, display, path);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, path);

    // "maxima" subpart y = d+a, "minima" subpart y = d-a.
    // When a flips sign the roles swap, so resolve the label dynamically.
    const name = () => ((type === 'maxima') === parentFn.getParams()['a'] >= 0 ? 'max' : 'min');

    // Sine: a * sin(b(x - c)) + d
    // Maxima at b(x - c) = π/2 + 2πn → x = c + π/(2b) + 2πn/b
    // Minima at b(x - c) = 3π/2 + 2πn → x = c + 3π/(2b) + 2πn/b
    const coords: [() => number, () => number] =
      type === 'maxima'
        ? [
            () => {
              const { b, c } = parentFn.getParams();
              return c + Math.PI / (2 * b) + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d + a;
            },
          ]
        : [
            () => {
              const { b, c } = parentFn.getParams();
              return c + (3 * Math.PI) / (2 * b) + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d - a;
            },
          ];

    const result = elementFactory.point({ coords, name, style: { ...style, visible } });
    const objects: GeometryElement[] = [result.point];
    if (result.label) {
      objects.push(result.label);
    }
    return objects;
  }

  private createCosinePoint(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[],
    parentFn: FunctionElement,
    n: number,
    type: 'maxima' | 'minima',
  ): GeometryElement[] {
    const { elementFactory, featureTheme } = context;
    const style = resolveFeatureStyle('point', this.featureType, context, display, path);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, path);

    // "maxima" subpart y = d+a, "minima" subpart y = d-a.
    // When a flips sign the roles swap, so resolve the label dynamically.
    const name = () => ((type === 'maxima') === parentFn.getParams()['a'] >= 0 ? 'max' : 'min');

    // Cosine: a * cos(b(x - c)) + d
    // Maxima at b(x - c) = 2πn → x = c + 2πn/b
    // Minima at b(x - c) = π + 2πn → x = c + π/b + 2πn/b
    const coords: [() => number, () => number] =
      type === 'maxima'
        ? [
            () => {
              const { b, c } = parentFn.getParams();
              return c + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d + a;
            },
          ]
        : [
            () => {
              const { b, c } = parentFn.getParams();
              return c + Math.PI / b + (2 * Math.PI * n) / b;
            },
            () => {
              const { a, d } = parentFn.getParams();
              return d - a;
            },
          ];

    const result = elementFactory.point({ coords, name, style: { ...style, visible } });
    const objects: GeometryElement[] = [result.point];
    if (result.label) {
      objects.push(result.label);
    }
    return objects;
  }
}

/** Build the full set of computed value keys for the extrema feature. */
function buildComputedValueKeys(extremaCount: number): string[] {
  const keys: string[] = ['count', 'x0', 'y0', 'maximaCount', 'minimaCount', 'maxY', 'minY'];
  const totalPoints = 2 * extremaCount + 1;
  for (let i = 0; i < totalPoints; i++) {
    keys.push(`maxima${i}X`, `maxima${i}Y`, `minima${i}X`, `minima${i}Y`);
  }
  return keys;
}

/** Get vertex X for either quadratic form. Standard form computes h = -b/(2a). */
function getQuadraticVertexX(fn: FunctionElement): number {
  const p = fn.getParams();
  if (fn.type === 'quadratic-vertex') {
    return p['h'];
  }
  const { h } = standardToVertex(p['a'], p['b'], p['c']);
  return h;
}

/** Get vertex Y for either quadratic form. Standard form computes k = c - b²/(4a). */
function getQuadraticVertexY(fn: FunctionElement): number {
  const p = fn.getParams();
  if (fn.type === 'quadratic-vertex') {
    return p['k'];
  }
  const { k } = standardToVertex(p['a'], p['b'], p['c']);
  return k;
}

/**
 * Get the discriminant of f'(x) for a cubic function.
 * For general form f'(x) = 3ax² + 2bx + c → disc = b² - 3ac
 * For vertex form f'(x) = 3a(x-h)² → disc is always 0 (no distinct extrema)
 */
function getCubicDiscriminant(fn: FunctionElement): number {
  const p = fn.getParams();
  if (fn.type === 'cubic') {
    // Vertex form a(x-h)³+k: f'(x) = 3a(x-h)², only root is x=h (inflection, not extremum)
    return -1;
  }
  // General form: disc of f'(x) = 3ax² + 2bx + c is (2b)² - 4(3a)(c) = 4(b² - 3ac)
  return p['b'] * p['b'] - 3 * p['a'] * p['c'];
}

/**
 * Get the x-coordinate of a cubic extremum.
 * f'(x) = 3ax² + 2bx + c = 0 → x = (-b ± √(b²-3ac)) / (3a)
 * x with -√ → f'' < 0 → local max; x with +√ → f'' > 0 → local min.
 */
function getCubicExtremaX(fn: FunctionElement, type: 'max' | 'min'): number {
  const p = fn.getParams();
  if (fn.type === 'cubic') {
    return NaN;
  }
  const disc = p['b'] * p['b'] - 3 * p['a'] * p['c'];
  if (disc < 0) {
    return NaN;
  }
  const sqrtDisc = Math.sqrt(disc);
  // -√ branch → local max, +√ branch → local min
  return type === 'max' ? (-p['b'] - sqrtDisc) / (3 * p['a']) : (-p['b'] + sqrtDisc) / (3 * p['a']);
}

/** Get the y-coordinate of a cubic extremum by evaluating f(x) at the critical point. */
function getCubicExtremaY(fn: FunctionElement, type: 'max' | 'min'): number {
  const x = getCubicExtremaX(fn, type);
  if (isNaN(x)) {
    return NaN;
  }
  return fn.evaluate(x);
}
